/**
 * @author Manifold Finance, Inc.
 * @date July, 12, 2023
 * @license GPL-3.0
 */

import AccountStatus from '@/components/enFOLD/AccountStatus';
import Background from '@/components/enFOLD/Background';
import BigWhiteButton from '@/components/general/BigWhiteButton';
import {
  CardInnerContainer,
  CardInnerContainerMana,
  CardInnerContainerManaInvites,
  CardInnerContainerWithdrawals,
  CardOuterContainer,
  CardOuterContainerWithdrawals,
  HeaderContainer,
  LogoImage,
  SmallCube,
} from '@/components/general/Card';
import ExtraInfoPluStakeButton from '@/components/general/ExtraInfoPluStakeButton';
import StakeCenterFold from '@/components/mana/StakeCenterFold';
import StakeTopFold, { PossibleModes } from '@/components/mana/StakeTopFold';
import StakeCenterFoldManaSidebar from '@/components/manaSidebar/StakeCenterFold';
import StakeTopFoldManaSidebar from '@/components/manaSidebar/StakeTopFold';
import { pointsAtom, PointsDetails } from '@/components/manaStarter/StakeCenterFold';
import { NEXT_PUBLIC_URL } from '@/constants/addresses';
import useIsMobileMana from '@/hooks/useIsMobileMana';
import DefaultLayout from '@/layout/DefaultLayout';
import { supabase } from '@/lib/supabaseClient';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { signIn, signOut, useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { use, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAccount } from 'wagmi';
import { UserData, UserProfile } from '../api/invites/getCurrentProfile';

function Header() {
  return (
    <HeaderContainer>
      <Link href="/">
        <LogoImage src="/svg/MEV Text White.svg" alt="mevETH" />
      </Link>
      <ExtraInfoPluStakeButton />
    </HeaderContainer>
  );
}
export const handleCopy = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    console.log('Text copied successfully!');
  } catch (err) {
    console.error('Failed to copy text:', err);
  }
};
export const generateTweetIntent = (data: UserData) => {
  if (data.user) {
    let inviteURL =
      'https://twitter.com/intent/post?via=mevdotio&text=Deposit%20ETH%20to%20get%20%24mevETH%20and%20earn%20some%20MEV%20Mana%2C%20here%20are%20some%20invites:%0A%0A';
    for (const invite of data.user.invites
      .filter((invite) => {
        return invite.redeemer_id === null;
      })
      .slice(0, 4)) {
      console.log(inviteURL);
      inviteURL += `${NEXT_PUBLIC_URL}/mana/invites/${invite.code}%0A`;
    }

    console.log(inviteURL);
    return inviteURL;
  }
  return '';
};

function Card() {
  const isMobile = useIsMobileMana();
  const [points, setPoints] = useAtom(pointsAtom);
  const [mode, setMode] = useState<PossibleModes>('Withdrawals');
  const [showInvites, setShowInvites] = useState(false);
  const [showPools, setShowPools] = useState(false);
  const [showVotes, setShowVotes] = useState(false);
  const [user, setUser] = useState<UserProfile | null>();
  const [userName, setUserName] = useState('');
  const { address } = useAccount();
  const { data: session, status } = useSession();
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [tweetIntent, setTweetIntent] = useState('');
  const router = useRouter();

  const updateProfile = async (hide_avatar?: boolean) => {
    setShowEditProfile(false);
    const res = await fetch('/api/invites/updateProfile', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userName: userName,
        hide_avatar,
        verified_token: user?.verified_token,
      }),
    });
    const data: UserData = await res.json();
    setUser(data.user);
    console.log('updated', data.user?.user_name);
    if (!data.user?.user_name) return;
    setUserName(data.user.user_name);
  };
  useEffect(() => {
    const getUserProfile = async (verified_token?: string | null) => {
      const res = await fetch('/api/invites/getCurrentProfile?verified_token=' + verified_token);
      const data: UserData = await res.json();
      if (data.message === 'No invite') {
        router.push('/mana/invites');
      }
      if (data.user === null) {
        router.push('/mana/starter');
      }
      setUser(data.user);
      if (data.user) {
        setTweetIntent(generateTweetIntent(data));
      }
      console.log('user-data', user, status);
      setUserName(getUserName());
    };
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const json = JSON.parse(storedUser);
      setUser(json);
      //@ts-ignore
      getUserProfile(json.verified_token);
    } else {
      getUserProfile('');
    }
  }, []);
  useEffect(() => {
    const checkAddress = async () => {
      if (address === undefined) return;
      const { data, error } = await supabase
        .from('total_points_materialized_view_detailed_votes')
        .select()
        .eq('address', address.toLowerCase())
        .single();
      console.log('data points detailed', data);
      if (!data?.total_points) {
        setPoints({
          address:
            address && address !== null ? address : '0x0000000000000000000000000000000000000000',
          total_points: 0,
          has_points: false,
          has_votes: false,
          has_lp: false,
        });
        return;
      }
      //@ts-ignore
      const pointsDetails: PointsDetails = data;
      setPoints(pointsDetails);
    };
    if (address) {
      checkAddress();
    }
  }, [address]);
  // const [stakeMode, setStakeMode] = useState<"Ready" | "Loading" | "Convert">(
  const getUserName = () => {
    if (!user) return 'anon';
    if (user.user_name) return user.user_name;
    if (user.first_name) return user.first_name;
    if (user.ens) return user.ens;
    if (user.metadata) return user.metadata.username;
    return 'anon';
  };
  const getUserAvatarUrl = () => {
    if (!user) return '';
    if (user.hide_avatar) return 'svg/anon.svg';
    if (user.avatar_url !== null && user.avatar_url) return user.avatar_url;
    if (user.twitter_image !== null && user.twitter_image) return user.twitter_image;

    return JSON.stringify(user.metadata);
  };
  if (!address || address === null) {
    return (
      <LayoutGroup>
        <CardOuterContainerWithdrawals
          initial={{ transform: 'scale(0.9)' }}
          animate={{ transform: 'scale(1)' }}
          exit={{ transform: 'scale(0.9)' }}
          layout
        >
          <TopFrame>
            <BigWhiteButton text="Connect" onClick={() => {}} />
          </TopFrame>
        </CardOuterContainerWithdrawals>
      </LayoutGroup>
    );
  }
  return (
    <LayoutGroup>
      <CardOuterContainerWithdrawals
        initial={{ transform: 'scale(0.9)' }}
        animate={{ transform: 'scale(1)' }}
        exit={{ transform: 'scale(0.9)' }}
        layout
      >
        {isMobile ? (
          <>
            <TopFrameMobile>
              <InsideFrame>
                <FrameRowMobile>
                  <div>
                    <FrameRowBigTitleMobile>MEV Mana Program</FrameRowBigTitleMobile>
                    <FrameRowSubTitle>
                      Stake ETH, provide liquidity and vote to earn more MEV Mana
                    </FrameRowSubTitle>
                  </div>

                  <div>
                    <FlexRowCenter>
                      <FrameRowManaBalanceTitle>
                        <Image
                          src="/svg/mana.svg"
                          width={24}
                          height={24}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />
                      </FrameRowManaBalanceTitle>

                      <Hmana>MEV Mana</Hmana>
                    </FlexRowCenter>

                    <FrameRowSubTitle>
                      <ManaBalanceLabel>Balance:</ManaBalanceLabel>
                      <ManaBalance>
                        {new Intl.NumberFormat('en-US').format(points.total_points)}
                      </ManaBalance>
                    </FrameRowSubTitle>
                  </div>
                </FrameRowMobile>
              </InsideFrame>
            </TopFrameMobile>
            <TopFrameRowsMobile>
              <FrameRowItemMobileContainer>
                <FrameRowItemMobile>
                  <ItemCheckbox>
                    <FlexRowCenter>
                      <Image
                        src={`/svg/mana${points.has_points ? '' : 'un'}checkedbox.svg`}
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                    </FlexRowCenter>
                  </ItemCheckbox>
                  <ItemLabelMobile>
                    <FlexRowCenter>
                      <Image
                        src="/svg/vote.svg"
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />

                      <ItemLabelTitle>Own mevETH</ItemLabelTitle>
                    </FlexRowCenter>
                  </ItemLabelMobile>
                </FrameRowItemMobile>
                <FrameColItemMobile>
                  <ItemInfo style={{ paddingTop: '10px' }}>Hold mevETH in your address</ItemInfo>
                  <ItemActionMobile>
                    <FlexRowCenter style={{ paddingTop: '30px' }}>
                      <ActionButton href="/stake">Deposit via mevRouter</ActionButton>
                    </FlexRowCenter>
                  </ItemActionMobile>
                </FrameColItemMobile>
              </FrameRowItemMobileContainer>
              <FrameRowItemMobileContainer>
                <FrameRowItemMobile>
                  <ItemCheckbox style={{ marginTop: '22px' }}>
                    <FlexRowCenter>
                      <Image
                        src={`/svg/mana${false ? '' : 'un'}checkedbox.svg`}
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                    </FlexRowCenter>
                  </ItemCheckbox>
                  <ItemLabelMobile>
                    <FlexRowCenter style={{ marginTop: '22px' }}>
                      <Image
                        src="/svg/diversifyeth.svg"
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />

                      <ItemLabelTitle>Diversify Ethereum</ItemLabelTitle>
                    </FlexRowCenter>
                  </ItemLabelMobile>
                </FrameRowItemMobile>

                <FrameColItemMobile>
                  <ItemInfo>
                    Help diversify ETH staking by defending Ethereum's credible neutrality and
                    censorship resistance. Deposit stETH and start earning MEV Mana today while we
                    convert stETH to mevETH. Redemption and deposit process back to your wallet can
                    take up to 14 days.
                  </ItemInfo>
                  <ItemAction>
                    <FlexRowCenter>
                      <ActionButton style={{ marginTop: '15px' }}>
                        coming soon
                        {/*Deposit stETH to get mevETH*/}
                      </ActionButton>
                    </FlexRowCenter>
                  </ItemAction>
                </FrameColItemMobile>
              </FrameRowItemMobileContainer>

              <FrameRowItemMobileContainer>
                <FrameRowItemMobile
                  onClick={() => {
                    setShowInvites(!showInvites);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <ItemCheckbox>
                    <FlexRowCenter>
                      <Image
                        src={`/svg/mana${
                          user?.invites &&
                          user?.invites.filter((invite) => {
                            return invite.redeemer_id;
                          }).length > 0
                            ? ''
                            : 'un'
                        }checkedbox.svg`}
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                    </FlexRowCenter>
                  </ItemCheckbox>
                  <ItemLabelMobile>
                    <FlexRowCenter>
                      <Image
                        src="/svg/invite.svg"
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />

                      <ItemLabelTitle>Invite others into your Guild</ItemLabelTitle>
                      <Image
                        src="/svg/up.svg"
                        width={12}
                        height={12}
                        alt="Mana"
                        style={{
                          marginLeft: 10,
                          transform: showInvites ? 'scale(-1)' : 'scale(1)',
                        }}
                      />
                    </FlexRowCenter>
                  </ItemLabelMobile>
                </FrameRowItemMobile>

                <FrameColItemMobile>
                  <ItemInfo>Refer others with your Guild invite code</ItemInfo>
                  <ItemActionMobile>
                    <FlexRowCenter></FlexRowCenter>
                    <ActionButton
                      style={{ marginTop: '15px' }}
                      href={tweetIntent}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Share your invites on 𝕏
                      <Image
                        src="/svg/right.svg"
                        width={16}
                        height={16}
                        alt="Mana"
                        style={{ marginLeft: 10 }}
                      />
                    </ActionButton>
                  </ItemActionMobile>
                </FrameColItemMobile>
              </FrameRowItemMobileContainer>
              <div style={{ display: showInvites ? '' : 'none' }}>
                {user?.invites?.map((invite) => (
                  <FrameRowItemMobileContainer key={invite.code}>
                    <FrameColItemMobile>
                      <ItemInviteCode>
                        <FlexRowCenter>
                          <Image
                            src="/svg/invitecircle.svg"
                            width={24}
                            height={24}
                            alt="Mana"
                            style={{ marginRight: 10 }}
                          />
                          <ItemLabelTitle
                            style={{ textDecoration: invite.redeemer_id ? 'line-through' : '' }}
                          >
                            {invite.code}
                          </ItemLabelTitle>
                        </FlexRowCenter>
                      </ItemInviteCode>

                      <ItemInfo style={{ marginTop: '10px', marginBottom: '10px' }}>
                        Refer others with your Guild invite code
                      </ItemInfo>
                    </FrameColItemMobile>
                    <FrameColItemMobile>
                      <ItemAction>
                        <FlexRowCenter style={{ paddingTop: '10px' }}>
                          <ActionButton
                            onClick={() => {
                              if (invite.redeemer_id) return;
                              handleCopy(NEXT_PUBLIC_URL + '/mana/invites/' + invite.code);
                            }}
                          >
                            {invite.redeemer_id ? 'Redeemed' : 'Copy Invite Code'}
                            <Image
                              src="/svg/copy.svg"
                              width={16}
                              height={16}
                              alt="Mana"
                              style={{ marginLeft: 10 }}
                            />
                          </ActionButton>
                        </FlexRowCenter>
                      </ItemAction>
                    </FrameColItemMobile>
                  </FrameRowItemMobileContainer>
                ))}
              </div>
              <FrameRowItemMobileContainer>
                <FrameRowItemMobile
                  onClick={() => {
                    setShowPools(!showPools);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <ItemCheckbox>
                    <FlexRowCenter>
                      <Image
                        src={`/svg/mana${points.has_lp ? '' : 'un'}checkedbox.svg`}
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                    </FlexRowCenter>
                  </ItemCheckbox>
                  <ItemLabelMobile>
                    <FlexRowCenter>
                      <Image
                        src="/svg/liquiditypools.svg"
                        width={85.333}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />

                      <ItemLabelTitle>Contribute to mevETH liquidity Pools</ItemLabelTitle>
                      <Image
                        src="/svg/up.svg"
                        width={12}
                        height={12}
                        alt="Mana"
                        style={{ marginLeft: 10, scale: showPools ? '-1' : '1' }}
                      />
                    </FlexRowCenter>
                  </ItemLabelMobile>
                </FrameRowItemMobile>

                <FrameColItemMobile>
                  <ItemInfo>Add to mevETH liquidity pools on Convex and Balancer</ItemInfo>
                  <ItemActionMobile>
                    <FlexRowCenter>
                      <ActionButton style={{ marginTop: '15px' }}>
                        Deposit via stakerBPT (coming soon)
                        <Image
                          src="/svg/right.svg"
                          width={16}
                          height={16}
                          alt="Mana"
                          style={{ marginLeft: 10 }}
                        />
                      </ActionButton>
                    </FlexRowCenter>
                  </ItemActionMobile>
                </FrameColItemMobile>
              </FrameRowItemMobileContainer>
              <div style={{ display: showPools ? 'none' : 'none' }}>
                <FrameRowItemMobileContainer>
                  <FrameColItemMobile>
                    <ItemCheckbox>
                      <FlexRowCenter>
                        <Image
                          src="/svg/manacheckedbox.svg"
                          width={32}
                          height={32}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />
                      </FlexRowCenter>
                    </ItemCheckbox>
                    <ItemLabel>
                      <FlexRowCenter>
                        <Image
                          src="/svg/mevethFraxpool.svg"
                          width={85.333}
                          height={32}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />

                        <ItemLabelTitle>LP - Curve mevETH / frxETH</ItemLabelTitle>
                      </FlexRowCenter>
                    </ItemLabel>
                  </FrameColItemMobile>
                  <FrameColItemMobile>
                    <ItemInfo>Add to Curve LP - link to blog post</ItemInfo>
                    <ItemAction>
                      <FlexRowCenter>
                        <ActionButton href="/meveth">
                          Deposit via stakerBPT etc...{' '}
                          <Image src="/svg/right.svg" width={16} height={16} alt="Mana" />
                        </ActionButton>
                      </FlexRowCenter>
                    </ItemAction>
                  </FrameColItemMobile>
                </FrameRowItemMobileContainer>
                <FrameRowItemMobileContainer>
                  <FrameColItemMobile>
                    <ItemCheckbox>
                      <FlexRowCenter>
                        <Image
                          src="/svg/manacheckedbox.svg"
                          width={32}
                          height={32}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />
                      </FlexRowCenter>
                    </ItemCheckbox>
                    <ItemLabel>
                      <FlexRowCenter>
                        <Image
                          src="/svg/mevethpool.svg"
                          width={85.333}
                          height={32}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />

                        <ItemLabelTitle>LP - Curve mevETH / WETH</ItemLabelTitle>
                      </FlexRowCenter>
                    </ItemLabel>
                  </FrameColItemMobile>
                  <FrameColItemMobile>
                    <ItemInfo>Add to Balancer LP - link to blog post</ItemInfo>
                    <ItemAction>
                      <FlexRowCenter>
                        <ActionButton href="/meveth">
                          Deposit via stakerBPT etc...{' '}
                          <Image src="/svg/right.svg" width={16} height={16} alt="Mana" />
                        </ActionButton>
                      </FlexRowCenter>
                    </ItemAction>
                  </FrameColItemMobile>
                </FrameRowItemMobileContainer>
              </div>

              <FrameRowItemMobileContainer>
                <FrameRowItemMobile
                  onClick={() => {
                    setShowVotes(!showVotes);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <ItemCheckbox>
                    <FlexRowCenter>
                      <Image
                        src={`/svg/mana${points.has_votes ? '' : 'un'}checkedbox.svg`}
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                    </FlexRowCenter>
                  </ItemCheckbox>
                  <ItemLabelMobile>
                    <FlexRowCenter>
                      <Image
                        src="/svg/vote.svg"
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />

                      <ItemLabelTitle>Vote for mevETH liquidity in pools</ItemLabelTitle>
                      <Image
                        src="/svg/up.svg"
                        width={12}
                        height={12}
                        alt="Mana"
                        style={{ marginLeft: 10, scale: showVotes ? '-1' : '1' }}
                      />
                    </FlexRowCenter>
                  </ItemLabelMobile>
                </FrameRowItemMobile>
                <FrameColItemMobile>
                  <ItemInfo>
                    Vote for mevETH liquidity in Curve, Convex, Balancer, Aura, or Staked yETH pools
                  </ItemInfo>
                  <ItemAction>
                    <FlexRowCenter>
                      <ActionButton href="/meveth" style={{ marginTop: '15px' }}>
                        Share links on Voting
                      </ActionButton>
                    </FlexRowCenter>
                  </ItemAction>
                </FrameColItemMobile>
              </FrameRowItemMobileContainer>
              <div style={{ display: showVotes ? '' : 'none' }}>
                <FrameRowItemMobileContainer>
                  <FrameColItemMobile>
                    <ItemInviteCode>
                      <FlexRowCenter>
                        <Image
                          src="/svg/yearn.svg"
                          width={24}
                          height={24}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />
                        <ItemLabelTitle>Yearn</ItemLabelTitle>
                      </FlexRowCenter>
                    </ItemInviteCode>

                    <ItemInfo style={{ marginTop: '10px', marginBottom: '10px' }}>
                      Vote for mevETH on Yearn st-yETH pools
                    </ItemInfo>
                  </FrameColItemMobile>
                  <FrameColItemMobile>
                    <ItemAction>
                      <FlexRowCenter style={{ paddingTop: '10px' }}>
                        <ActionButton href="https://snapshot.org/#/ylsd.eth">
                          Vote on Yearn{' '}
                          <Image
                            src="/svg/right.svg"
                            width={16}
                            height={16}
                            alt="Mana"
                            style={{ marginLeft: 10 }}
                          />
                        </ActionButton>
                      </FlexRowCenter>
                    </ItemAction>
                  </FrameColItemMobile>
                </FrameRowItemMobileContainer>
                <FrameRowItemMobileContainer>
                  <FrameColItemMobile>
                    <ItemInviteCode>
                      <FlexRowCenter>
                        <Image
                          src="/svg/curve-mana.svg"
                          width={24}
                          height={24}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />
                        <ItemLabelTitle>Curve</ItemLabelTitle>
                      </FlexRowCenter>
                    </ItemInviteCode>

                    <ItemInfo style={{ marginTop: '10px', marginBottom: '10px' }}>
                      Vote for mevETH on Curve
                    </ItemInfo>
                  </FrameColItemMobile>
                  <FrameColItemMobile>
                    <ItemAction>
                      <FlexRowCenter style={{ paddingTop: '10px' }}>
                        <ActionButton href="https://snapshot.org/#/ylsd.eth">
                          Vote on Curve{' '}
                          <Image
                            src="/svg/right.svg"
                            width={16}
                            height={16}
                            alt="Mana"
                            style={{ marginLeft: 10 }}
                          />
                        </ActionButton>
                      </FlexRowCenter>
                    </ItemAction>
                  </FrameColItemMobile>
                </FrameRowItemMobileContainer>
                <FrameRowItemMobileContainer>
                  <FrameColItemMobile>
                    <ItemInviteCode>
                      <FlexRowCenter>
                        <Image
                          src="/svg/convex.svg"
                          width={24}
                          height={24}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />
                        <ItemLabelTitle>Convex</ItemLabelTitle>
                      </FlexRowCenter>
                    </ItemInviteCode>

                    <ItemInfo style={{ marginTop: '10px', marginBottom: '10px' }}>
                      Vote for mevETH on Convex
                    </ItemInfo>
                  </FrameColItemMobile>
                  <FrameColItemMobile>
                    <ItemAction>
                      <FlexRowCenter style={{ paddingTop: '10px' }}>
                        <ActionButton href="https://snapshot.org/#/ylsd.eth">
                          Vote on Convex{' '}
                          <Image
                            src="/svg/right.svg"
                            width={16}
                            height={16}
                            alt="Mana"
                            style={{ marginLeft: 10 }}
                          />
                        </ActionButton>
                      </FlexRowCenter>
                    </ItemAction>
                  </FrameColItemMobile>
                </FrameRowItemMobileContainer>

                <FrameRowItemMobileContainer>
                  <FrameColItemMobile>
                    <ItemInviteCode>
                      <FlexRowCenter>
                        <Image
                          src="/svg/balancer-mana.svg"
                          width={24}
                          height={24}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />
                        <ItemLabelTitle>Convex</ItemLabelTitle>
                      </FlexRowCenter>
                    </ItemInviteCode>

                    <ItemInfo style={{ marginTop: '10px', marginBottom: '10px' }}>
                      Vote for mevETH on Balancer/Aurora
                    </ItemInfo>
                  </FrameColItemMobile>
                  <FrameColItemMobile>
                    <ItemAction>
                      <FlexRowCenter style={{ paddingTop: '10px' }}>
                        <ActionButton href="https://snapshot.org/#/ylsd.eth">
                          Vote on Balancer{' '}
                          <Image
                            src="/svg/right.svg"
                            width={16}
                            height={16}
                            alt="Mana"
                            style={{ marginLeft: 10 }}
                          />
                        </ActionButton>
                      </FlexRowCenter>
                    </ItemAction>
                  </FrameColItemMobile>
                </FrameRowItemMobileContainer>
              </div>
            </TopFrameRowsMobile>
            {/* 
             <TopFrameRows>
              
              
              

              
           
              
            </TopFrameRows>*/}
          </>
        ) : (
          <>
            <TopFrame>
              <InsideFrame>
                <FrameRow>
                  <div>
                    <FrameRowBigTitle>MEV Mana Program</FrameRowBigTitle>
                    <FrameRowSubTitle>
                      Stake ETH, provide liquidity and vote to earn more MEV Mana
                    </FrameRowSubTitle>
                  </div>
                </FrameRow>
                <FrameRow>
                  <div>
                    <FlexRowCenter>
                      <FrameRowManaBalanceTitle>
                        <Image
                          src="/svg/mana.svg"
                          width={24}
                          height={24}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />
                      </FrameRowManaBalanceTitle>

                      <Hmana>MEV Mana</Hmana>
                    </FlexRowCenter>

                    <FlexRowCenter>
                      <FrameRowSubTitle>
                        <ManaBalanceLabel>Balance:</ManaBalanceLabel>
                        <ManaBalance>
                          {new Intl.NumberFormat('en-US').format(points.total_points)}
                        </ManaBalance>
                      </FrameRowSubTitle>
                      <FrameRowSubTitle>
                        <ManaUserName>
                          <FlexRowCenter>
                            {showEditProfile ? '' : `@${user?.user_name || getUserName()} `}{' '}
                            <span
                              onClick={() => {
                                setUserName(getUserName());
                                setShowEditProfile(!showEditProfile);
                              }}
                              style={{
                                marginLeft: '10px',
                                display: showEditProfile ? 'none' : '',
                                cursor: 'pointer',
                              }}
                            >
                              [Edit]
                            </span>
                            {showEditProfile && (
                              <>
                                <input
                                  type="text"
                                  value={userName}
                                  style={{ background: 'white', margin: '0px 10px' }}
                                  onChange={(e) => {
                                    setUserName(e.target.value);
                                  }}
                                />
                                <button
                                  onClick={() => {
                                    updateProfile();
                                  }}
                                >
                                  save
                                </button>{' '}
                                <a
                                  style={{
                                    cursor: 'pointer',
                                    display: showEditProfile ? '' : 'none',
                                  }}
                                  onClick={() => {
                                    setShowEditProfile(false);
                                  }}
                                >
                                  [cancel]
                                </a>
                              </>
                            )}
                            <img
                              src="/svg/signout.svg"
                              width={16}
                              height={16}
                              alt="Mana"
                              style={{ marginLeft: 20, cursor: 'pointer' }}
                              onClick={() => {
                                signOut();
                                localStorage.removeItem('user');
                                router.push('/');
                              }}
                            />
                          </FlexRowCenter>
                        </ManaUserName>
                        <ManaUserOptions>
                          <FlexRowCenter>
                            <img
                              src={getUserAvatarUrl()}
                              width={32}
                              height={32}
                              alt="Mana"
                              style={{ marginRight: 10 }}
                            />
                            <a
                              style={{ fontWeight: 100, fontSize: 16, cursor: 'pointer' }}
                              onClick={() => {
                                updateProfile(!user?.hide_avatar);
                              }}
                            >
                              [hide]
                            </a>
                          </FlexRowCenter>
                        </ManaUserOptions>
                      </FrameRowSubTitle>
                    </FlexRowCenter>
                  </div>
                </FrameRow>
              </InsideFrame>
            </TopFrame>
            <TopFrameRows>
              <FrameRowItemHeader>
                <ItemLabelHeader>
                  <FlexRowCenter>
                    <ItemLabelTitle>WAYS TO EARN MEV Mana</ItemLabelTitle>
                  </FlexRowCenter>
                </ItemLabelHeader>
                <ItemInfoHeader>INFO</ItemInfoHeader>
              </FrameRowItemHeader>
              <FrameRowItem>
                <ItemCheckbox>
                  <FlexRowCenter>
                    <Image
                      src={`/svg/mana${points.has_points ? '' : 'un'}checkedbox.svg`}
                      width={32}
                      height={32}
                      alt="Mana"
                      style={{ marginRight: 10 }}
                    />
                  </FlexRowCenter>
                </ItemCheckbox>
                <ItemLabel>
                  <FlexRowCenter>
                    <Image
                      src="/svg/vote.svg"
                      width={32}
                      height={32}
                      alt="Mana"
                      style={{ marginRight: 10 }}
                    />

                    <ItemLabelTitle>Own mevETH</ItemLabelTitle>
                  </FlexRowCenter>
                </ItemLabel>
                <ItemInfo style={{ paddingTop: '10px' }}>Hold mevETH in your address</ItemInfo>
                <ItemAction>
                  <FlexRowCenter style={{ paddingTop: '10px' }}>
                    <ActionButton href="/stake">Deposit via mevRouter</ActionButton>
                  </FlexRowCenter>
                </ItemAction>
              </FrameRowItem>
              <FrameRowItem style={{ paddingTop: '10px', paddingBottom: '10px' }}>
                <ItemCheckbox style={{ marginTop: '22px' }}>
                  <FlexRowCenter>
                    <Image
                      src={`/svg/mana${false ? '' : 'un'}checkedbox.svg`}
                      width={32}
                      height={32}
                      alt="Mana"
                      style={{ marginRight: 10 }}
                    />
                  </FlexRowCenter>
                </ItemCheckbox>
                <ItemLabel>
                  <FlexRowCenter style={{ marginTop: '22px' }}>
                    <Image
                      src="/svg/diversifyeth.svg"
                      width={32}
                      height={32}
                      alt="Mana"
                      style={{ marginRight: 10 }}
                    />

                    <ItemLabelTitle>Diversify Ethereum</ItemLabelTitle>
                  </FlexRowCenter>
                </ItemLabel>
                <ItemInfo>
                  Help diversify ETH staking by defending Ethereum's credible neutrality and
                  censorship resistance. Deposit stETH and start earning MEV Mana today while we
                  convert stETH to mevETH. Redemption and deposit process back to your wallet can
                  take up to 14 days.
                </ItemInfo>
                <ItemAction>
                  <FlexRowCenter>
                    <ActionButton style={{ marginTop: '15px' }}>
                      coming soon
                      {/*Deposit stETH to get mevETH*/}
                    </ActionButton>
                  </FlexRowCenter>
                </ItemAction>
              </FrameRowItem>
              <FrameRowItem
                onClick={() => {
                  setShowInvites(!showInvites);
                }}
                style={{ cursor: 'pointer' }}
              >
                <ItemCheckbox>
                  <FlexRowCenter>
                    <Image
                      src={`/svg/mana${
                        user?.invites &&
                        user?.invites.filter((invite) => {
                          return invite.redeemer_id;
                        }).length > 0
                          ? ''
                          : 'un'
                      }checkedbox.svg`}
                      width={32}
                      height={32}
                      alt="Mana"
                      style={{ marginRight: 10 }}
                    />
                  </FlexRowCenter>
                </ItemCheckbox>
                <ItemLabel>
                  <FlexRowCenter>
                    <Image
                      src="/svg/invite.svg"
                      width={32}
                      height={32}
                      alt="Mana"
                      style={{ marginRight: 10 }}
                    />

                    <ItemLabelTitle>Invite others into your Guild</ItemLabelTitle>
                    <Image
                      src="/svg/up.svg"
                      width={12}
                      height={12}
                      alt="Mana"
                      style={{ marginRight: 10, transform: showInvites ? 'scale(-1)' : 'scale(1)' }}
                    />
                  </FlexRowCenter>
                </ItemLabel>
                <ItemInfo>Refer others with your Guild invite code</ItemInfo>
                <ItemAction>
                  <FlexRowCenter></FlexRowCenter>
                  <ActionButton
                    style={{ marginTop: '15px' }}
                    href={tweetIntent}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Share your invites on 𝕏
                    <Image
                      src="/svg/right.svg"
                      width={16}
                      height={16}
                      alt="Mana"
                      style={{ marginLeft: 10 }}
                    />
                  </ActionButton>
                </ItemAction>
              </FrameRowItem>
              <div style={{ display: showInvites ? '' : 'none' }}>
                {user?.invites?.map((invite) => (
                  <FrameRowItemChildren key={invite.code}>
                    <ItemInviteCode>
                      <FlexRowCenter>
                        <Image
                          src="/svg/invitecircle.svg"
                          width={24}
                          height={24}
                          alt="Mana"
                          style={{ marginRight: 10 }}
                        />
                        <ItemLabelTitle
                          style={{ textDecoration: invite.redeemer_id ? 'line-through' : '' }}
                        >
                          {invite.code}
                        </ItemLabelTitle>
                      </FlexRowCenter>
                    </ItemInviteCode>

                    <ItemInfo>Refer others with your Guild invite code</ItemInfo>
                    <ItemAction>
                      <FlexRowCenter style={{ paddingTop: '10px' }}>
                        <ActionButton
                          onClick={() => {
                            if (invite.redeemer_id) return;
                            handleCopy(NEXT_PUBLIC_URL + '/mana/invites/' + invite.code);
                          }}
                        >
                          {invite.redeemer_id ? 'Redeemed' : 'Copy Invite Code'}
                          <Image
                            src="/svg/copy.svg"
                            width={16}
                            height={16}
                            alt="Mana"
                            style={{ marginLeft: 10 }}
                          />
                        </ActionButton>
                      </FlexRowCenter>
                    </ItemAction>
                  </FrameRowItemChildren>
                ))}
              </div>

              <FrameRowItem
                onClick={() => {
                  setShowPools(!showPools);
                }}
                style={{ cursor: 'pointer' }}
              >
                <ItemCheckbox>
                  <FlexRowCenter>
                    <Image
                      src={`/svg/mana${points.has_lp ? '' : 'un'}checkedbox.svg`}
                      width={32}
                      height={32}
                      alt="Mana"
                      style={{ marginRight: 10 }}
                    />
                  </FlexRowCenter>
                </ItemCheckbox>
                <ItemLabel>
                  <FlexRowCenter>
                    <Image
                      src="/svg/liquiditypools.svg"
                      width={85.333}
                      height={32}
                      alt="Mana"
                      style={{ marginRight: 10 }}
                    />

                    <ItemLabelTitle>Contribute to mevETH liquidity Pools</ItemLabelTitle>
                    <Image
                      src="/svg/up.svg"
                      width={12}
                      height={12}
                      alt="Mana"
                      style={{ marginRight: 10, scale: showPools ? '-1' : '1' }}
                    />
                  </FlexRowCenter>
                </ItemLabel>
                <ItemInfo>Add to mevETH liquidity pools on Convex and Balancer</ItemInfo>
                <ItemAction>
                  <FlexRowCenter>
                    <ActionButton>
                      coming soon...
                      <Image
                        src="/svg/right.svg"
                        width={16}
                        height={16}
                        alt="Mana"
                        style={{ marginLeft: 10 }}
                      />
                    </ActionButton>
                  </FlexRowCenter>
                </ItemAction>
              </FrameRowItem>
              <div style={{ display: showPools ? 'none' : 'none' }}>
                <FrameRowItemChildren>
                  <ItemCheckbox>
                    <FlexRowCenter>
                      <Image
                        src="/svg/manacheckedbox.svg"
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                    </FlexRowCenter>
                  </ItemCheckbox>
                  <ItemLabel>
                    <FlexRowCenter>
                      <Image
                        src="/svg/mevethFraxpool.svg"
                        width={85.333}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />

                      <ItemLabelTitle>LP - Curve mevETH / frxETH</ItemLabelTitle>
                    </FlexRowCenter>
                  </ItemLabel>
                  <ItemInfo>Add to Curve LP - link to blog post</ItemInfo>
                  <ItemAction>
                    <FlexRowCenter>
                      <ActionButton href="/meveth">
                        Deposit via stakerBPT etc...{' '}
                        <Image src="/svg/right.svg" width={16} height={16} alt="Mana" />
                      </ActionButton>
                    </FlexRowCenter>
                  </ItemAction>
                </FrameRowItemChildren>
                <FrameRowItemChildren>
                  <ItemCheckbox>
                    <FlexRowCenter>
                      <Image
                        src="/svg/manacheckedbox.svg"
                        width={32}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                    </FlexRowCenter>
                  </ItemCheckbox>
                  <ItemLabel>
                    <FlexRowCenter>
                      <Image
                        src="/svg/mevethpool.svg"
                        width={85.333}
                        height={32}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />

                      <ItemLabelTitle>LP - Curve mevETH / WETH</ItemLabelTitle>
                    </FlexRowCenter>
                  </ItemLabel>
                  <ItemInfo>Add to Balancer LP - link to blog post</ItemInfo>
                  <ItemAction>
                    <FlexRowCenter>
                      <ActionButton href="/meveth">
                        Deposit via stakerBPT etc...{' '}
                        <Image src="/svg/right.svg" width={16} height={16} alt="Mana" />
                      </ActionButton>
                    </FlexRowCenter>
                  </ItemAction>
                </FrameRowItemChildren>
              </div>

              <FrameRowItem
                onClick={() => {
                  setShowVotes(!showVotes);
                }}
                style={{ cursor: 'pointer' }}
              >
                <ItemCheckbox>
                  <FlexRowCenter>
                    <Image
                      src={`/svg/mana${points.has_votes ? '' : 'un'}checkedbox.svg`}
                      width={32}
                      height={32}
                      alt="Mana"
                      style={{ marginRight: 10 }}
                    />
                  </FlexRowCenter>
                </ItemCheckbox>
                <ItemLabel>
                  <FlexRowCenter>
                    <Image
                      src="/svg/vote.svg"
                      width={32}
                      height={32}
                      alt="Mana"
                      style={{ marginRight: 10 }}
                    />

                    <ItemLabelTitle>Vote for mevETH liquidity in pools</ItemLabelTitle>
                    <Image
                      src="/svg/up.svg"
                      width={12}
                      height={12}
                      alt="Mana"
                      style={{ marginRight: 10, scale: showVotes ? '-1' : '1' }}
                    />
                  </FlexRowCenter>
                </ItemLabel>
                <ItemInfo>
                  Vote for mevETH liquidity in Curve, Convex, Balancer, Aura, or Staked yETH pools
                </ItemInfo>
                <ItemAction>
                  <FlexRowCenter>
                    <ActionButton href="/meveth">Share links on Voting</ActionButton>
                  </FlexRowCenter>
                </ItemAction>
              </FrameRowItem>
              <div style={{ display: showVotes ? '' : 'none' }}>
                <FrameRowItemChildren>
                  <ItemInviteCode>
                    <FlexRowCenter>
                      <Image
                        src="/svg/yearn.svg"
                        width={24}
                        height={24}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                      <ItemLabelTitle>Yearn</ItemLabelTitle>
                    </FlexRowCenter>
                  </ItemInviteCode>

                  <ItemInfo>Vote for mevETH on Yearn st-yETH pools</ItemInfo>
                  <ItemAction>
                    <FlexRowCenter style={{ paddingTop: '10px' }}>
                      <ActionButton href="https://snapshot.org/#/ylsd.eth">
                        Vote on Yearn{' '}
                        <Image
                          src="/svg/right.svg"
                          width={16}
                          height={16}
                          alt="Mana"
                          style={{ marginLeft: 10 }}
                        />
                      </ActionButton>
                    </FlexRowCenter>
                  </ItemAction>
                </FrameRowItemChildren>
                <FrameRowItemChildren>
                  <ItemInviteCode>
                    <FlexRowCenter>
                      <Image
                        src="/svg/curve-mana.svg"
                        width={24}
                        height={24}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                      <ItemLabelTitle>Curve</ItemLabelTitle>
                    </FlexRowCenter>
                  </ItemInviteCode>

                  <ItemInfo>Vote for mevETH on Curve</ItemInfo>
                  <ItemAction>
                    <FlexRowCenter style={{ paddingTop: '10px' }}>
                      <ActionButton href="https://curve.fi">
                        Vote on Curve{' '}
                        <Image
                          src="/svg/right.svg"
                          width={16}
                          height={16}
                          alt="Mana"
                          style={{ marginLeft: 10 }}
                        />
                      </ActionButton>
                    </FlexRowCenter>
                  </ItemAction>
                </FrameRowItemChildren>
                <FrameRowItemChildren>
                  <ItemInviteCode>
                    <FlexRowCenter>
                      <Image
                        src="/svg/convex.svg"
                        width={24}
                        height={24}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                      <ItemLabelTitle>Convex</ItemLabelTitle>
                    </FlexRowCenter>
                  </ItemInviteCode>

                  <ItemInfo>Vote for mevETH on Convex</ItemInfo>
                  <ItemAction>
                    <FlexRowCenter style={{ paddingTop: '10px' }}>
                      <ActionButton href="https://convexfinance.com/">
                        Vote on Convex{' '}
                        <Image
                          src="/svg/right.svg"
                          width={16}
                          height={16}
                          alt="Mana"
                          style={{ marginLeft: 10 }}
                        />
                      </ActionButton>
                    </FlexRowCenter>
                  </ItemAction>
                </FrameRowItemChildren>
                <FrameRowItemChildren>
                  <ItemInviteCode>
                    <FlexRowCenter>
                      <Image
                        src="/svg/balancer-mana.svg"
                        width={24}
                        height={24}
                        alt="Mana"
                        style={{ marginRight: 10 }}
                      />
                      <ItemLabelTitle>Balancer/Aurora</ItemLabelTitle>
                    </FlexRowCenter>
                  </ItemInviteCode>

                  <ItemInfo>Vote for mevETH on Balancer/Aurora</ItemInfo>
                  <ItemAction>
                    <FlexRowCenter style={{ paddingTop: '10px' }}>
                      <ActionButton href="https://balancer.fi/">
                        Vote on Balancer{' '}
                        <Image
                          src="/svg/right.svg"
                          width={16}
                          height={16}
                          alt="Mana"
                          style={{ marginLeft: 10 }}
                        />
                      </ActionButton>
                    </FlexRowCenter>
                  </ItemAction>
                </FrameRowItemChildren>
              </div>
            </TopFrameRows>
          </>
        )}

        <AccountStatus />
        {/*     <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <CardInnerContainerMana>
            <AnimatePresence>
              <StakeCenterFoldManaSidebar mode={mode} setMode={setMode} />
            </AnimatePresence>
          </CardInnerContainerMana>

          <CardInnerContainerManaInvites style={{ height: 700 }}>
            <StakeTopFold mode={mode} setMode={setMode} />
            <AnimatePresence>
              <StakeCenterFold mode={mode} setMode={setMode} />
            </AnimatePresence>
          </CardInnerContainerManaInvites>
        </div>*/}
      </CardOuterContainerWithdrawals>
    </LayoutGroup>
  );
}

export default function StakePage() {
  return (
    <DefaultLayout>
      <Header />
      <Card />
      <Background />
    </DefaultLayout>
  );
}

const TopFrame = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-center;
  padding: 0px;
  gap: 24px;

  width: 1296px;
  height: 158px;
`;

const TopFrameMobile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-center;
  padding: 4px;
  gap: 24px;

  width: 100%;
`;

const InsideFrame = styled.div`
  display: flex;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
`;

const FrameRow = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 32px;
  gap: 16px;

  width: 636px;
  height: 158px;
  margin-right: 30px;

  background: rgba(79, 79, 79, 0.23);
  border: 1px solid rgba(79, 79, 79, 0.19);
  backdrop-filter: blur(6px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 10px;
`;

const FrameRowMobile = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px;
  gap: 16px;

  width: 100%;

  background: rgba(79, 79, 79, 0.23);
  border: 1px solid rgba(79, 79, 79, 0.19);
  backdrop-filter: blur(6px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 10px;
`;
const FrameRowBigTitle = styled.div`
  font-size: 48px;
  font-weight: 600;
`;

const FrameRowBigTitleMobile = styled.div`
  font-size: 24px;
  font-weight: 600;
`;

const FrameRowSubTitle = styled.div`
  margin-top: 16px;
`;

const FrameRowManaBalanceTitle = styled.div`
  font-size: 28px;
`;

const FlexRowCenter = styled.div`
  display: flex;

  flex-direction: row;

  align-items: center;
`;

const Hmana = styled.h2`
  font-size: 16px;
  font-weight: 500;
`;

const ManaBalance = styled.div`
  font-size: 24px;
  font-weight: 600;
  margin-top: 10px;
`;
const ManaBalanceLabel = styled.div`
  margin-top: -5px;
  font-weight: 500;
`;
const ManaUserOptions = styled.div`
  font-size: 24px;
  font-weight: 600;
  margin-top: 10px;

  margin-left: 50px;
`;
const ManaUserName = styled.div`
  margin-top: -5px;
  font-weight: 500;
  margin-left: 50px;
`;

const TopFrameRows = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-center;
  padding: 0px;
  margin-top: 24px;
`;
const TopFrameRowsMobile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-center;
  padding: 0px;
  margin-top: 24px;
  width: 100%;
`;
const FrameRowItem = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 32px;
  gap: 16px;

  width: 1296px;
  min-height: 74px;
  margin-bottom: 15px;
  background: #12151c;
  border: 1px solid rgba(79, 79, 79, 0.19);
  backdrop-filter: blur(6px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 10px;
`;

const FrameRowItemMobile = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  align-items: flex-start;
`;

const FrameRowItemMobileContainer = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  gap: 16px;

  width: 100%;
  min-height: 74px;
  margin-bottom: 15px;
  background: #12151c;
  border: 1px solid rgba(79, 79, 79, 0.19);
  backdrop-filter: blur(6px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 10px;
`;

const FrameColItemMobile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const FrameRowItemChildren = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 20px 12px 12px 12px;
  gap: 16px;

  width: 1236px;
  min-height: 54px;
  margin-bottom: 15px;
  margin-left: 50px;
  background: #12151c;
  border: 1px solid rgba(79, 79, 79, 0.19);
  backdrop-filter: blur(6px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 10px;
`;

const FrameRowItemChildrenMobile = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 20px 12px 12px 12px;
  gap: 16px;

  width: 50%;
  min-height: 54px;
  margin-bottom: 15px;
  margin-left: 50px;
`;

const FrameRowItemHeader = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 32px;
  gap: 16px;

  width: 1296px;
  min-height: 74px;
  /* Note: backdrop-filter has minimal browser support */
`;

const ItemInviteCode = styled.div`
  flex-basis: 31%;
`;
const ItemCheckbox = styled.div`
  flex-basis: 1%;
  height: 32px;
`;
const ItemLabel = styled.div`
  flex-basis: 30%;
`;
const ItemLabelMobile = styled.div`
  flex-basis: 99%;
`;
const ItemLabelHeader = styled.div`
  flex-basis: 31%;
  font-weight: 500;
  font-size: 14px;
`;
const ItemLabelTitle = styled.h2`
  font-weight: 600;
  font-size: 16px;
`;
const ItemInfo = styled.div`
  flex-basis: 50%;
  font-size: 14px;
  padding: 0;
`;
const ItemInfoHeader = styled.div`
  flex-basis: 50%;
  padding: 0;
  font-weight: 500;
  font-size: 14px;
`;
const ItemAction = styled.div`
  flex-basis: 30%;
`;
const ItemActionMobile = styled.div`
  flex-basis: 100%;
`;

const ActionButton = styled.a`
  background: #ffffff;
  color: #000000;
  border-radius: 40px;
  height: 38px;
  padding: 20px 36px 20px 36px;
  justify-content: space-between;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 300;
  cursor: pointer;
  margin-top: -15px;

  box-shadow: 0px 4px 12px 4px rgba(255, 255, 255, 0.15);

  &:hover {
    transform: scale(1.05);
    box-shadow-color: rgba(255, 255, 255, 0.25);
  }
  transition: transform 0.2s ease-in-out;
`;
